// Componente para captura de leads com integração GTM e FullFunnel
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Send, Check, ShoppingCart, ExternalLink } from 'lucide-react';
import { Card } from './ui/Card';
import { persistUtmsFromLocation, pickUtmsFallback, norm } from '../utils/utm';
import { fadeUp } from './anim/variants';
// Removido import de API key - agora usamos API route segura

interface LeadFormProps {
  className?: string;
}

export function LeadForm({ className = '' }: LeadFormProps) {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Persistir UTMs ao carregar o componente
  useEffect(() => {
    persistUtmsFromLocation();
    
    // Limpar UTMs expirados
    const cleanup = () => {
      try {
        const store = JSON.parse(localStorage.getItem('utm_store') || '{}');
        if (store._timestamp) {
          const ageInDays = (Date.now() - store._timestamp) / (1000 * 60 * 60 * 24);
          if (ageInDays > 90) {
            localStorage.removeItem('utm_store');
          }
        }
      } catch {
        console.warn('Erro ao limpar UTMs expirados');
      }
    };
    
    cleanup();
  }, []);

  const handleInputChange = (field: string, value: string) => {
    const sanitizedValue = value
        .replace(/[<>"']/g, '')
      .replace(/&/g, 'e')
      .slice(0, 150);
    
    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstname.trim()) {
      newErrors.firstname = 'Nome é obrigatório';
    }
    
    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Sobrenome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'WhatsApp é obrigatório';
    } else if (formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'WhatsApp inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    
    try {
      if (typeof window !== 'undefined') {
        // Inicializar dataLayer se não existir
        window.dataLayer = window.dataLayer || [];
        
        // Normalizar dados
        const name = norm.name(`${formData.firstname} ${formData.lastname}`);
        const email = norm.email(formData.email);
        const { ddd, phone } = norm.phoneSplit(formData.phone);
        
        // UTMs com fallback (URL -> storage)
        const utm = pickUtmsFallback();
        
        // Push de evento (compatível com gatilho FORM_SUBMISSION)
        window.dataLayer.push({
          event: 'generate_lead',
          form_submit: true,
          user_firstname: formData.firstname,
          user_lastname: formData.lastname,
          user_email: email,
          user_phone: formData.phone,
          ...utm
        });
        
        // Evento enviado para GTM (sem logs sensíveis)

        // Processar lead no FullFunnel via API route segura
        const leadData = {
          firstName: formData.firstname,
          lastName: formData.lastname,
          name: name,
          email: email,
          phone: formData.phone,
          utm: {
            utm_source: utm.utm_source || undefined,
            utm_medium: utm.utm_medium || undefined,
            utm_campaign: utm.utm_campaign || undefined,
            utm_term: utm.utm_term || undefined,
            utm_content: utm.utm_content || undefined,
          },
        };

        // Enviar para FullFunnel via API route segura (server-side)
        // O token nunca é exposto ao cliente
        try {
          const apiResponse = await fetch('/api/leads', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-lead-secret': 'insumo-2026-leads', // Proteção anti-spam
            },
            body: JSON.stringify(leadData),
          });

          if (!apiResponse.ok) {
            // Log mínimo - não expor detalhes do erro
            console.error('❌ Erro ao processar lead');
          }
        } catch (apiError) {
          // Log mínimo - não expor detalhes do erro
          console.error('❌ Erro ao conectar com API');
        }
        
        // Montar URL do checkout com PREFILL + UTMs
        const buyer = new URLSearchParams({
          name: name,
          email: email,
          phoneac: ddd || '',
          phonenumber: phone || ''
        });
        
        const utmParams = new URLSearchParams(utm);
        
        // Link do checkout do Hotmart
        const finalCheckoutUrl = `https://pay.hotmart.com/F102214853I?checkoutMode=10&${buyer.toString()}&${utmParams.toString()}`;
        
        // Salvar URL e mostrar tela de confirmação
        setCheckoutUrl(finalCheckoutUrl);
        setShowConfirmation(true);
        setIsSubmitting(false);
      }
    } catch (error) {
      setErrors({ general: 'Erro inesperado. Tente novamente.' });
      setIsSubmitting(false);
    }
  };

  // Função para redirecionar ao Hotmart
  const handleFinalize = () => {
    if (checkoutUrl) {
      try {
        window.location.href = checkoutUrl;
      } catch (redirectError) {
        window.open(checkoutUrl, '_blank');
      }
    }
  };

  // Tela de confirmação após envio do formulário
  if (showConfirmation) {
    return (
      <div className={`p-8 ${className}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          {/* Ícone de sucesso - círculo verde com check */}
          <div className="flex justify-center">
            <div className="w-24 h-24 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              <div className="w-16 h-16 border-4 border-white rounded-full flex items-center justify-center">
                <Check className="w-10 h-10 text-white stroke-[3]" />
              </div>
            </div>
          </div>

          {/* Mensagem de sucesso */}
          <div className="space-y-3">
            <h3 className="text-3xl font-bold text-gray-800">
              Dados salvos!
            </h3>
            <p className="text-gray-600 text-lg leading-relaxed">
              Agora finalize sua compra para<br />
              garantir seu Kit Jardim da Fé!
            </p>
          </div>

          {/* Botão principal - Finalizar Agora com gradiente laranja */}
          <motion.button
            onClick={handleFinalize}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="w-full h-16 rounded-2xl px-6 font-bold text-xl shadow-xl text-white flex items-center justify-center gap-3 transition-all"
            style={{
              background: 'linear-gradient(135deg, #F59E0B 0%, #F97316 50%, #EA580C 100%)',
              boxShadow: '0 8px 24px rgba(249, 115, 22, 0.4)'
            }}
          >
            <ShoppingCart className="w-6 h-6" />
            Finalizar Agora
            <ExternalLink className="w-5 h-5" />
          </motion.button>

          {/* Texto de redirecionamento */}
          <p className="text-sm text-gray-500">
            Você será redirecionado para o Hotmart
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <Card className={`p-8 ${className}`} glassmorphism={true}>
      <form id="lead-form" method="POST" noValidate onSubmit={handleSubmit}>
        <div className="space-y-6">
          <motion.div variants={fadeUp}>
            <label htmlFor="form-field-firstname" className="block text-sm font-medium text-gray-700 mb-2">
              Nome
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="form-field-firstname"
                name="form_fields[firstname]"
                type="text"
                required
                value={formData.firstname}
                onChange={(e) => handleInputChange('firstname', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white text-gray-900 placeholder:text-gray-500 ${
                      errors.firstname ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                placeholder="Seu nome"
              />
            </div>
            {errors.firstname && (
              <p className="mt-1 text-sm text-red-600">{errors.firstname}</p>
            )}
          </motion.div>

          <motion.div variants={fadeUp}>
            <label htmlFor="form-field-lastname" className="block text-sm font-medium text-gray-700 mb-2">
              Sobrenome
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="form-field-lastname"
                name="form_fields[lastname]"
                type="text"
                required
                value={formData.lastname}
                onChange={(e) => handleInputChange('lastname', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white text-gray-900 placeholder:text-gray-500 ${
                      errors.lastname ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                placeholder="Seu sobrenome"
              />
            </div>
            {errors.lastname && (
              <p className="mt-1 text-sm text-red-600">{errors.lastname}</p>
            )}
          </motion.div>

          <motion.div variants={fadeUp}>
            <label htmlFor="form-field-email" className="block text-sm font-medium text-gray-700 mb-2">
              E-mail
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="form-field-email"
                name="form_fields[email]"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white text-gray-900 placeholder:text-gray-500 ${
                      errors.email ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-primary-500'
                    }`}
                placeholder="seu@email.com"
              />
            </div>
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </motion.div>

          <motion.div variants={fadeUp}>
            <label htmlFor="form-field-phone" className="block text-sm font-medium text-gray-700 mb-2">
              DDD + WhatsApp
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                id="form-field-phone"
                name="form_fields[phone]"
                type="tel"
                inputMode="tel"
                required
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors bg-white text-gray-900 placeholder:text-gray-500 ${
                  errors.phone ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-primary-500'
                }`}
                placeholder="11999999999"
              />
            </div>
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </motion.div>

          <motion.div variants={fadeUp}>
            <button
              id="lead-submit"
              type="submit"
              disabled={isSubmitting}
              className="w-full h-14 rounded-xl px-5 font-bold shadow-lg bg-primary-600 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 min-h-[48px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:bg-primary-700 transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processando...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Quero fazer parte
                </>
              )}
            </button>
          </motion.div>
        </div>
      </form>
    </Card>
  );
}
